import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { weekdayLabel } from '../../timesheets/calendarDates.js';
import { useMyWeek } from '../../timesheets/useMyWeek.js';

import { HOME_CARD_LINK, HomeCard } from './HomeCard.js';

/**
 * "My week" — the hours a crew member has booked, and whether the week is signed off.
 *
 * ⚠ THE GRID IS NOT ALREADY YOURS. `GET /api/v1/timesheets/week-grid` scopes its rows to one
 * employee ONLY when `user.role === 'Employee'`; every other role gets the whole tenant back,
 * ordered by name. Home picks this layout by CAPABILITY, not by role, so a Manager stripped of
 * `reports.view` and `jobs.view` lands here — and this card used to print `rows[0]`, the
 * alphabetically first employee's hours, under the heading "My week". `useMyWeek` picks the row
 * matching `scope.employeeId` instead, and the punch tiles above read the same one.
 *
 * EVERY HOUR HERE IS THE SERVER'S. The worker applies the flat six-hour lunch rule before it
 * answers; nothing in this file adds, subtracts or rounds a figure that reaches a pay record.
 */
export function CrewWeek() {
  const { t } = useTranslation();
  const week = useMyWeek(true);
  const { row, dates, pendingEdits } = week;

  return (
    <HomeCard
      headingId="crew-week-heading"
      title={t('home:crew.week.heading')}
      action={
        <Link to="/timesheets" search={{}} className={HOME_CARD_LINK}>
          {t('home:crew.week.openTimesheets')}
        </Link>
      }
    >
      {week.isPending ? (
        <p className="mt-3 text-muted-foreground text-sm">{t('common:status.loading')}</p>
      ) : week.isError ? (
        <div role="alert" className="mt-3 flex items-center justify-between gap-3 text-sm">
          <span className="text-destructive">{t('home:crew.week.unavailable')}</span>
          {/*
            A dead end here used to be the whole state: the card said it could not load your hours
            and left you to reload the page. The label names WHICH thing it retries, because the
            punch tiles above this card can be offering two more of these at the same time.
          */}
          <button
            type="button"
            onClick={week.refetch}
            aria-label={t('home:crew.week.retry')}
            className="font-medium text-foreground underline underline-offset-4"
          >
            {t('common:error.retry')}
          </button>
        </div>
      ) : (
        <>
          {/*
            An em dash, never `0h`, when the grid carries no row for this person. The punch ledger
            prints the same em dash from the same source — a zero would be a claim about a week
            nobody has answered for.
          */}
          <p className="mt-3 font-mono font-semibold text-4xl text-foreground tabular-nums tracking-tight">
            {row ? t('home:crew.week.hours', { hours: row.totalHours }) : '—'}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {t('home:crew.week.entries', { count: row?.entryCount ?? 0 })}
            {' · '}
            {row?.isApprovedWeek
              ? t('home:crew.week.approved')
              : t('home:crew.week.awaitingApproval')}
          </p>

          {/*
            The seven days, on one fixed grid so the columns line up whether a day holds 0 or 10.5.
            A day with no hours keeps its slot rather than collapsing, because a gap in the middle
            of the week is the thing somebody is looking for.
          */}
          {dates.length > 0 ? (
            <ul className="mt-4 grid grid-cols-7 gap-1" aria-label={t('home:crew.week.daysLabel')}>
              {dates.map((date) => {
                const hours = row?.days?.[date] ?? 0;
                return (
                  <li key={date} className="rounded-md bg-secondary/40 px-1 py-2 text-center">
                    <span className="block text-2xs text-muted-foreground uppercase">
                      {weekdayLabel(t, date)}
                    </span>
                    <span className="mt-1 block font-mono text-foreground text-sm tabular-nums">
                      {hours > 0 ? hours : '—'}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {/*
            Only shown when there IS one. A permanent "0 pending" row would be a line of furniture
            the reader learns to skip, and the one week it matters they would skip it too.
          */}
          {pendingEdits > 0 ? (
            <p className="mt-4 text-foreground text-sm">
              {t('home:crew.week.pendingEdits', { count: pendingEdits })}
            </p>
          ) : null}
        </>
      )}
    </HomeCard>
  );
}
